/**
 * Ticket Lifecycle v2 — Slice 4C
 *
 * Pure helpers for custody dwell-time aging. Used by the FOH queue to
 * surface visual warnings when a ticket has been waiting too long in
 * `pending_advisor_accept`.
 *
 * Pure: no clock reads, no I/O. Callers inject `referenceTime` to keep
 * the function deterministic under test (and trivially memoizable).
 *
 * Boundary: this module imports neither scheduling, timer, parking,
 * rollover, bay, nor invariants modules. Display-only.
 */

/**
 * Threshold table. Hardcoded for slice 4C per product spec; future
 * sub-slices may make these shop-configurable via a settings table.
 */
export const CUSTODY_AGING_THRESHOLDS_MS = {
  warning: 5 * 60_000,   // 5 minutes
  urgent: 10 * 60_000,   // 10 minutes
} as const;

export type CustodyAgingLevel = 'normal' | 'warning' | 'urgent';

/**
 * Returns the visual aging level for a ticket sitting in a pending
 * custody state. `'normal'` means below the warning threshold;
 * `'warning'` is 5–10 min; `'urgent'` is 10 min or more.
 *
 * Returns `'normal'` when either timestamp is unparseable or when the
 * computed elapsed time is negative (clock skew defence).
 */
export function getPendingAcceptAgingLevel(
  enteredAt: string,
  referenceTime: string,
): CustodyAgingLevel {
  const startMs = Date.parse(enteredAt);
  const refMs = Date.parse(referenceTime);
  if (!Number.isFinite(startMs) || !Number.isFinite(refMs)) return 'normal';
  const elapsed = refMs - startMs;
  if (elapsed < 0) return 'normal';
  if (elapsed >= CUSTODY_AGING_THRESHOLDS_MS.urgent) return 'urgent';
  if (elapsed >= CUSTODY_AGING_THRESHOLDS_MS.warning) return 'warning';
  return 'normal';
}

/**
 * CUS-1 — per-state custody dwell thresholds.
 *
 * Slice 4C only time-graded `pending_advisor_accept`. CUS-1 extends
 * grading to the other dwell states that previously surfaced no timing
 * signal:
 *   - `pending_tech_accept` (BOH-return waiting for a tech to accept) —
 *     a return that stalls is exactly the silent-stall failure mode, so
 *     it carries the same 5/10-min urgency as advisor-accept.
 *   - `with_advisor` (advisor actively holding the ticket) — "active"
 *     work, not a queue wait, so it grades on a longer 20/40-min dwell
 *     to flag tickets parked on the advisor's desk.
 *
 * `with_tech` (BOH idle, normal working state) is intentionally never
 * graded. Unknown states return `'normal'`.
 */
export const CUSTODY_DWELL_THRESHOLDS_MS: Record<
  string,
  { warning: number; urgent: number }
> = {
  pending_advisor_accept: { warning: 5 * 60_000, urgent: 10 * 60_000 },
  pending_tech_accept: { warning: 5 * 60_000, urgent: 10 * 60_000 },
  with_advisor: { warning: 20 * 60_000, urgent: 40 * 60_000 },
};

/**
 * Returns the visual aging level for a ticket dwelling in a custody
 * state, using per-state thresholds (CUSTODY_DWELL_THRESHOLDS_MS).
 * States with no threshold entry (e.g. `with_tech`) and unparseable /
 * skewed timestamps return `'normal'`. Pure — caller injects
 * `referenceTime`.
 */
export function getCustodyDwellAgingLevel(
  status: string,
  enteredAt: string,
  referenceTime: string,
): CustodyAgingLevel {
  const thresholds = CUSTODY_DWELL_THRESHOLDS_MS[status];
  if (!thresholds) return 'normal';
  const startMs = Date.parse(enteredAt);
  const refMs = Date.parse(referenceTime);
  if (!Number.isFinite(startMs) || !Number.isFinite(refMs)) return 'normal';
  const elapsed = refMs - startMs;
  if (elapsed < 0) return 'normal';
  if (elapsed >= thresholds.urgent) return 'urgent';
  if (elapsed >= thresholds.warning) return 'warning';
  return 'normal';
}
